import { useEffect, useMemo, useRef, useState } from 'react'
import * as Model from 'api/model'
import { useParams } from 'react-router-dom'
import Menu from 'components/Menu'
import MenuItem from 'components/MenuItem'
import ExportTableMenuItem from 'components/ExportTableMenuItem'
import FilterTableMenuItem from 'components/FilterTableMenuItem'
import Table from 'components/Table'
import { Log } from 'helpers/log'
import useConfirmation from 'hooks/useConfirmation'
import moment from 'moment/min/moment-with-locales'
import useNotification from 'hooks/useNotification'
import DocumentViewer from 'components/DocumentViewer'
import { t } from '@lingui/core/macro'
import * as signalR from '@microsoft/signalr'

const ClaimDocuments = () => {
  const columns = useMemo(
    () => [
      { label: '', accessor: 'type', type: 'documentType' },
      {
        label: t`Name / Type`,
        accessor: 'name',
        type: 'document',
      },
      {
        label: t`Size`,
        accessor: 'size',
        type: 'fileSize',
      },
      {
        label: t`Originated`,
        accessor: 'originatedTimestamp',
        type: 'datetime',
      },
      {
        label: t`Uploaded`,
        accessor: 'uploadedTimestamp',
        type: 'datetime',
      },
    ],
    []
  )

  const claimApi = useMemo(() => new Model.ClaimClient(process.env.REACT_APP_API_URL), [])

  const tableRef = useRef<{ handleExport: () => void } | null>(null)
  const fileUploadRef = useRef<HTMLInputElement | null>(null)
  const { confirm } = useConfirmation()
  const { notify } = useNotification()
  const { uniqueID } = useParams()

  const [claim, setClaim] = useState<Model.Claim>()
  const [documents, setDocuments] = useState<Model.Document[]>()
  const [filterTerms, setFilterTerms] = useState('')
  const [clickedDocument, setClickedDocument] = useState<Model.Document>()
  const [uploadingDocument, setUploadingDocument] = useState<any>()

  const confirmDelete = (clickedDocument: Model.Document) => {
    if (!handleDelete) {
      return
    }

    confirm(t`Confirm deletion`, t`Are you sure you want to delete this document?`, () =>
      handleDelete(clickedDocument)
    )
  }

  useEffect(() => {
    if (uploadingDocument === undefined) {
      document.querySelectorAll('[progress]').forEach((element) => {
        element.removeAttribute('progress')
        element.removeAttribute('style')
      })
      return
    }

    if (uploadingDocument.percentComplete === 100) {
      setTimeout(() => {
        setUploadingDocument(undefined)
      }, 2000)
    }

    const tr = document.getElementById(`row-${uploadingDocument.uniqueID}`)

    if (!tr) {
      return
    }

    const currentProgress = tr.getAttribute('progress')
    const currentProgressNum = currentProgress ? parseFloat(currentProgress) : -1

    // Only update if progress doesn't exist or new progress is greater
    if (
      currentProgressNum === -1 ||
      uploadingDocument.percentComplete > currentProgressNum
    ) {
      tr.setAttribute('progress', uploadingDocument.percentComplete.toString())
      tr.setAttribute(
        'style',
        `background-image: linear-gradient(to right, var(--color-highlight) ${uploadingDocument.percentComplete}%, var(--color-white) 0%);`
      )

      const statusElement = tr.querySelector('.full-name div:nth-child(3)')
      if (statusElement) {
        statusElement.innerHTML = uploadingDocument.description
      }
    }
  }, [uploadingDocument])

  const handleFileChange = async (event: any) => {
    handleUpload(event.target.files[0], moment(event.target.files[0].lastModified))
    fileUploadRef.current!.value = ''
  }

  const handleRowClick = async (document: Model.Document) => {
    setClickedDocument(document)
  }

  const handleDelete = async (document: Model.Document) => {
    try {
      await claimApi.tombstoneDocument(claim!.uniqueID, document.uniqueID)
      await reload()
    } catch (error: any) {
      Log.add(JSON.stringify(error))
    }
  }

  const handleUpload = async (file: File, timestamp: moment.Moment) => {
    if (file) {
      const documentUniqueID = crypto.randomUUID()

      let fileParameter: Model.FileParameter = {
        data: file,
        fileName: file.name ?? 'Unknown',
      }

      try {
        const dummy: Model.Document = {
          uniqueID: documentUniqueID,
          name: file.name,
          size: file.size,
          originatedTimestamp: timestamp,
          type: Model.DocumentType.PDF,
        } as Model.Document

        setDocuments([...documents!, dummy])
        setUploadingDocument({
          uniqueID: documentUniqueID,
          percentComplete: 0,
          description: 'Uploading',
        })

        await claimApi.uploadDocument(
          claim!.uniqueID,
          documentUniqueID,
          timestamp,
          fileParameter
        )
      } catch (error: any) {
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          return
        }
        const apiError = JSON.parse(error.response)

        switch (apiError?.errorCodeName) {
          case Model.ErrorCode.DocumentHashAlreadyExists:
            notify(t`Error`, t`A file with that hash already exists`)
            break
          default:
            Log.add(JSON.stringify(apiError))
            break
        }
      } finally {
        await reload()
      }
    }
  }

  const reload = async () => {
    if (uniqueID === undefined) {
      return
    }

    await claimApi.getClaim(uniqueID).then((result) => {
      setClaim(result)
      setDocuments(result.documents.filter((x) => x.tombstonedTimestamp === undefined))
    })
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueID])

  useEffect(() => {
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(process.env.REACT_APP_SIGNALR_URL + '/hubs/documentupload', {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .configureLogging(signalR.LogLevel.Error)
      .withAutomaticReconnect()
      .build()

    hubConnection.start().catch((err) => Log.add(err))

    hubConnection.onreconnected((connectionId) => {
      Log.add('signalr reconnected')
    })

    hubConnection.onreconnecting((error) => {
      Log.add('signalr error: ' + JSON.stringify(error))
    })

    hubConnection.onclose((e) => {
      Log.add('signalr connection closed')
    })

    hubConnection.on(
      'SetDocumentUploadProgress',
      (documentUniqueID: string, percentComplete: number, description: string) => {
        setUploadingDocument({
          uniqueID: documentUniqueID,
          percentComplete: percentComplete,
          description: description,
        })
      }
    )

    // Cleanup function to stop the connection when component unmounts
    return () => {
      hubConnection.stop()
    }
  }, []) // Empty dependency array since we only want this to run once

  return (
    claim &&
    documents && (
      <>
        <div className="header">
          {t`Documents for claim`} {claim.externalID}
        </div>
        <Menu>
          <MenuItem label="Return to claim" icon="ArrowLeft" linkTo=".." />
          <MenuItem
            label={t`Add document`}
            icon="SquarePlus"
            onClick={() => fileUploadRef.current?.click()}
          />
          {documents.length > 0 ? (
            <>
              <MenuItem
                label={t`Chat with claim`}
                icon="WandMagicSparkles"
                linkTo={`/claims/${uniqueID}/chats`}></MenuItem>
              <ExportTableMenuItem onClick={() => tableRef.current?.handleExport()} />
              <FilterTableMenuItem
                filterTerms={filterTerms}
                onChange={(terms) => setFilterTerms(terms)}
              />
            </>
          ) : (
            <></>
          )}
        </Menu>
        <input
          type="file"
          accept=".pdf"
          ref={fileUploadRef}
          onChange={handleFileChange}
          style={{ visibility: 'hidden', width: '0' }}
        />
        <Table
          ref={tableRef}
          id="claim-documents-table"
          name={t`Claim documents`}
          keyField="uniqueID"
          columns={columns}
          sourceData={documents}
          isPropertyBarVisible={false}
          filterTerms={filterTerms}
          onDelete={confirmDelete}
          onRowClick={handleRowClick}
          initialSortColumn={'name'}></Table>
        <DocumentViewer
          theDocument={clickedDocument}
          claimUniqueID={uniqueID}
          citations={undefined}
          onCancel={() => {
            setClickedDocument(undefined)
          }}></DocumentViewer>
      </>
    )
  )
}

export default ClaimDocuments
